<template>
  <div class="pb-8 md:pb-12 bg-slate-50 font-sans">
    <!-- Premium Hero Section -->
    <section class="relative bg-slate-900 text-white overflow-hidden min-h-[480px] md:min-h-[500px] flex items-center mt-0">
      <!-- Background Image with Parallax effect -->
      <div class="absolute inset-0 z-0">
        <NuxtImg :src="cmsStore.home.hero.image" 
             class="w-full h-full object-cover object-center opacity-40 transform scale-105" 
             alt="Industrial Fire Protection"
             width="2500" height="1250"
             format="webp" preload fetchpriority="high" loading="eager" />
        <div class="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/80 to-transparent"></div>
        <div class="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-50 to-transparent opacity-100 z-10 pointer-events-none"></div>
      </div>
      
      <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20 flex flex-col justify-center pt-24 md:pt-0">
        <div class="max-w-3xl space-y-5 md:space-y-8">
          <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
            <span class="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            <span class="text-xs font-bold uppercase tracking-widest text-white/90">{{ cmsStore.home.hero.subtitle }}</span>
          </div>
          
          <h1 class="text-4xl sm:text-3xl md:text-5xl font-bold tracking-tighter leading-[1.1] md:leading-[1.05] text-white drop-shadow-2xl break-words">
             {{ cmsStore.home.hero.title.split('.')[0] }} <br class="hidden md:block"/>
             <span v-if="cmsStore.home.hero.title.split('.')[1]" class="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-400">{{ cmsStore.home.hero.title.split('.')[1] }}</span>
          </h1>
          
          <p class="text-base md:text-lg text-slate-300 font-normal max-w-xl leading-relaxed drop-shadow-sm">
            {{ cmsStore.home.hero.description }}
          </p>
          
          <div class="flex flex-row flex-wrap sm:flex-nowrap items-center gap-2 md:gap-4 pt-4 pb-24 md:pb-0 relative z-30">
            <NuxtLink :to="cmsStore.home.hero.primaryBtnLink" class="group relative px-3 md:px-5 py-2 md:py-3 bg-red-600 hover:bg-red-700 text-white text-[11px] sm:text-base font-bold rounded-lg overflow-hidden shadow-[0_8px_30px_-10px_rgba(220,38,38,0.6)] hover:shadow-[0_8px_30px_-5px_rgba(220,38,38,0.8)] transition-all duration-300 flex items-center gap-1.5 md:gap-2 justify-center whitespace-nowrap text-center">
              <span class="relative z-10 text-white">{{ cmsStore.home.hero.primaryBtnText }}</span>
              <component :is="ArrowRightIcon" class="w-3.5 h-3.5 md:w-4 md:h-4 relative z-10 group-hover:translate-x-1 transition-transform" />
            </NuxtLink>
            <NuxtLink :to="cmsStore.home.hero.secondaryBtnLink" class="px-3 md:px-5 py-2 md:py-3 bg-white hover:bg-slate-100 text-slate-900 text-[11px] sm:text-base font-bold rounded-lg transition-all duration-300 flex items-center justify-center shadow-sm whitespace-nowrap text-center">
              {{ cmsStore.home.hero.secondaryBtnText }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>



    <!-- Featured Products (Premium Cards) -->
    <section class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 overflow-hidden">
      <div class="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
        <div>
          <div class="text-primary font-bold tracking-widest text-[10px] uppercase mb-2 flex items-center gap-2">
            <span class="w-6 h-px bg-primary"></span> Curated Selection
          </div>
          <h2 class="text-xl md:text-2xl font-bold text-slate-900 tracking-tight leading-tight">Featured Equipment</h2>
        </div>
        <NuxtLink to="/shop" class="group flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors uppercase tracking-wider mb-2">
          View Collection <span class="w-8 h-8 rounded-full bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-colors"><component :is="ArrowRightIcon" class="w-4 h-4" /></span>
        </NuxtLink>
      </div>

      <!-- Skeleton Loading State -->
      <div v-if="productStore.loading && productStore.products.length === 0" class="flex flex-col items-center justify-center py-8 md:py-12 bg-white rounded-2xl border border-dashed border-slate-200">
        <LoadingSpinner />
      </div>

      <div v-else-if="productStore.loading" class="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <div v-for="i in 4" :key="i" class="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 flex flex-col h-full animate-pulse">
          <div class="h-32 bg-slate-100"></div>
          <div class="p-4 flex flex-col flex-1 space-y-2">
            <div class="h-2 w-1/3 bg-slate-100 rounded"></div>
            <div class="h-4 w-full bg-slate-100 rounded"></div>
            <div class="h-4 w-2/3 bg-slate-100 rounded"></div>
            <div class="mt-auto pt-2 flex justify-between items-center">
              <div class="h-5 w-1/2 bg-slate-100 rounded"></div>
              <div class="h-9 w-9 bg-slate-100 rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <div v-for="product in featuredProducts" :key="product.id" class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-500 border border-slate-100 flex flex-col relative h-full">
          
          <div class="absolute top-3 left-3 z-10 flex flex-col items-start gap-1">
            <span v-if="product.featured" class="bg-slate-900 text-white text-[9px] font-bold px-2 py-1 rounded-full uppercase tracking-wider shadow-sm">Featured</span>
            <span v-if="product.offerProduct" class="bg-red-500 text-white text-[9px] font-bold px-2 py-1 rounded-full uppercase tracking-wider shadow-sm">Special Offer</span>
            <span v-if="product.futureProduct" class="bg-blue-600 text-white text-[9px] font-bold px-2 py-1 rounded-full uppercase tracking-wider shadow-sm">Coming Soon</span>
          </div>

          <div class="absolute top-3 right-3 z-10">
            <button aria-label="Add to Wishlist" class="w-8 h-8 bg-white/80 backdrop-blur-md rounded-full flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-white transition-all shadow-sm focus:outline-none">
              <component :is="HeartIcon" class="w-5 h-5" />
            </button>
          </div>
          
          <NuxtLink :to="`/product/${product.slug}`" class="h-32 bg-slate-50/50 p-3 flex items-center justify-center overflow-hidden block w-full relative border-b border-slate-50">
             <div class="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none"></div>
             <NuxtImg :src="product.image" :alt="product.name" width="200" height="200" loading="lazy" format="webp" class="max-w-[70%] max-h-full object-contain group-hover:scale-110 transition-transform duration-700 ease-out drop-shadow-sm" />
          </NuxtLink>
          
          <div class="p-3 flex flex-col flex-1 bg-white">
            <div class="text-[8px] font-bold text-slate-400 tracking-widest uppercase mb-0.5">{{ product.category }}</div>
            <NuxtLink :to="`/product/${product.slug}`" class="font-bold text-slate-800 hover:text-primary transition-colors text-sm leading-tight line-clamp-none md:line-clamp-2 mb-1.5 flex-1">
              {{ product.name }}
            </NuxtLink>
                
                <div class="flex items-center gap-1 mb-2">
                  <div class="flex text-amber-400">
                    <component :is="StarIcon" v-for="i in 5" :key="i" class="w-3 h-3 fill-current" />
                  </div>
                  <span class="text-[10px] font-semibold text-slate-400 ml-1">(Premium)</span>
                </div>
                
                <div class="flex items-center justify-between mt-auto pt-2 border-t border-slate-50">
                        <div class="flex items-center gap-1">
                           <span class="text-[10px] font-bold text-slate-400 uppercase">QAR</span>
                           <span class="text-base font-bold text-slate-900 tracking-tighter">{{ (product.price || 0).toFixed(2) }}</span>
                        </div>
                  <button aria-label="Add to Cart" @click.stop="addToCart(product)" class="w-9 h-9 rounded-[0.8rem] bg-slate-100 flex items-center justify-center text-slate-900 hover:text-white hover:bg-primary group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300 focus:outline-none">
                    <component :is="ShoppingCartIcon" class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
    </section>


  <!-- Modern Services Section -->
    <section class="py-8 md:py-16 bg-white relative overflow-hidden">
      <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="text-center mb-10 max-w-3xl mx-auto">
          <span class="text-primary font-bold tracking-widest text-[10px] uppercase mb-2 block">Enterprise Solutions</span>
          <h2 class="text-2xl md:text-3xl font-bold mb-4 text-slate-900 tracking-tight">{{ cmsStore.home.services.title }}</h2>
          <p class="text-slate-500 text-sm md:text-base font-light">{{ cmsStore.home.services.description }}</p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 px-2">
          <div v-for="(block, idx) in cmsStore.home.services.blocks" :key="idx" class="bg-slate-50 p-6 rounded-2xl border border-slate-100 hover:shadow-lg transition-all duration-500 group relative">
            <div class="w-12 h-12 bg-white shadow-sm text-primary rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-white transition-colors duration-300">
              <component :is="getServiceIcon(block.icon)" class="w-6 h-6" />
            </div>
            <h3 class="text-lg font-bold mb-3 text-slate-900">{{ block.title }}</h3>
            <p class="text-slate-500 text-sm leading-relaxed mb-6">{{ block.description }}</p>
            <NuxtLink :to="block.link" class="inline-flex items-center font-bold text-primary group-hover:gap-2 transition-all uppercase tracking-wider text-sm">
              Discover <component :is="ArrowRightIcon" class="w-4 h-4 ml-1" />
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Brands Logo Section -->
    <section class="py-8 md:py-16 bg-slate-50 border-t border-slate-200 overflow-hidden relative">
      <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="text-center mb-10">
            <span class="text-[9px] font-bold uppercase tracking-wider text-red-600 mb-2 block">Our Partners</span>
            <h2 class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">World-Class Equipment Brands</h2>
            <div class="w-16 h-1 bg-red-600 mx-auto mt-3 rounded-full"></div>
        </div>

        <!-- Auto-Moving Marquee Container -->
        <div class="flex overflow-hidden group w-full relative" style="mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);">
            <div class="flex animate-marquee shrink-0 items-center gap-8 md:gap-16 pr-8 md:pr-16">
                <NuxtLink 
                    v-for="brand in productStore.brands" 
                    :key="brand.id" 
                    :to="`/shop?brand=${encodeURIComponent(brand.name)}`"
                    class="flex items-center justify-center transition-all duration-500 hover:scale-105 w-32 md:w-48 shrink-0"
                >
                    <NuxtImg :src="brand.logo" :alt="brand.name" width="150" height="80" loading="lazy" format="webp" class="max-w-full max-h-16 md:max-h-20 object-contain drop-shadow-sm hover:drop-shadow-xl" />
                </NuxtLink>
            </div>
            <div class="flex animate-marquee shrink-0 items-center gap-8 md:gap-16 pr-8 md:pr-16" aria-hidden="true">
                <NuxtLink 
                    v-for="brand in productStore.brands" 
                    :key="brand.id + '-dup'" 
                    :to="`/shop?brand=${encodeURIComponent(brand.name)}`"
                    class="flex items-center justify-center transition-all duration-500 hover:scale-105 w-32 md:w-48 shrink-0"
                >
                    <NuxtImg :src="brand.logo" :alt="brand.name" width="150" height="80" loading="lazy" format="webp" class="max-w-full max-h-16 md:max-h-20 object-contain drop-shadow-sm hover:drop-shadow-xl" />
                </NuxtLink>
            </div>
        </div>
      </div>
    </section>

    <!-- Trust Badges -->
    <section class="py-8 md:py-12 border-t border-slate-200 bg-white">
      <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-x divide-slate-100">
          <div v-for="(badge, idx) in cmsStore.home.trustBadges?.badges" :key="idx" class="px-2 flex flex-col items-center">
            <component :is="getBadgeIcon(badge.icon)" class="w-6 h-6 text-slate-300 mb-2" />
            <h4 class="font-bold text-slate-900 text-sm mb-1">{{ badge.title }}</h4>
            <p class="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{{ badge.subtitle }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { 
  Flame as FlameIcon, 
  Shield as ShieldIcon, 
  Zap as ZapIcon, 
  Wind as WindIcon, 
  Layers as LayersIcon, 
  HeartPulse as HeartPulseIcon, 
  Lightbulb as LightbulbIcon, 
  Droplets as DropletsIcon,
  Package as PackageIcon,
  Star as StarIcon,
  Settings as SettingsIcon,
  ClipboardCheck as ClipboardCheckIcon,
  GraduationCap as GraduationCapIcon,
  ShieldCheck as ShieldCheckIcon,
  Truck as TruckIcon,
  PenTool as ToolIcon,
  Medal as MedalIcon,
  MailOpen as MailOpenIcon,
  ArrowRight as ArrowRightIcon,
  Heart as HeartIcon,
  Plus as PlusIcon,
  ShoppingCart as ShoppingCartIcon
} from 'lucide-vue-next'
import { useProductStore, categoryIcons as categoryIconsMap } from '~/stores/products'
import { useCartStore } from '~/stores/cart'
import { useCMSStore } from '~/stores/cms'

const productStore = useProductStore()
const cartStore = useCartStore()
const cmsStore = useCMSStore()

onMounted(async () => {
    if (productStore.brands.length === 0) {
        await productStore.fetchBrands()
    }
    if (productStore.products.length === 0) {
        await productStore.fetchProducts()
    }
    await cmsStore.fetchHomeCMS()
})

const featuredProducts = computed(() => {
  const featured = productStore.products.filter((p: any) => p.featured)
  // If no products are marked as featured, fallback to non-best-sellers
  return (featured.length > 0 ? featured : productStore.products.filter((p: any) => !p.bestSeller)).slice(0, 8)
})

const getIconComponent = (name: string) => {
  const iconName = categoryIconsMap[name] || 'Package'
  switch (iconName) {
    case 'Flame': return FlameIcon
    case 'Shield': return ShieldIcon
    case 'Zap': return ZapIcon
    case 'Wind': return WindIcon
    case 'Layers': return LayersIcon
    case 'HeartPulse': return HeartPulseIcon
    case 'Lightbulb': return LightbulbIcon
    case 'Droplets': return DropletsIcon
    case 'Package': return PackageIcon
    default: return PackageIcon
  }
}

const getServiceIcon = (name: string) => {
    switch (name) {
        case 'Settings': return SettingsIcon
        case 'ClipboardCheck': return ClipboardCheckIcon
        case 'GraduationCap': return GraduationCapIcon
        default: return SettingsIcon
    }
}

const getBadgeIcon = (name: string) => {
    switch (name) {
        case 'ShieldCheck': return ShieldCheckIcon
        case 'Truck': return TruckIcon
        case 'Tool': return ToolIcon
        case 'Medal': return MedalIcon
        default: return ShieldCheckIcon
    }
}

const addToCart = (product: any) => {
  cartStore.addItem(product)
}

useSeoMeta({
  title: 'Fire Alarm Supplies & Safety Equipment in Qatar | Alarm Supplies',
  description: 'Shop certified fire alarm equipment, extinguishers & smoke detectors in Qatar. QCDD-compliant products trusted by fire safety professionals. Order today.',
  ogTitle: 'Fire Alarm Supplies & Safety Equipment in Qatar | Alarm Supplies',
  ogDescription: 'Shop certified fire alarm equipment, extinguishers & smoke detectors in Qatar. QCDD-compliant products trusted by fire safety professionals. Order today.',
  ogImage: 'https://alarmsupplies.qa/images/logo.png',
  ogUrl: 'https://alarmsupplies.qa',
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: 'Fire Alarm Supplies & Safety Equipment in Qatar | Alarm Supplies',
  twitterDescription: 'Shop certified fire alarm equipment, extinguishers & smoke detectors in Qatar. QCDD-compliant products trusted by fire safety professionals. Order today.',
  twitterImage: 'https://alarmsupplies.qa/images/logo.png',
})
</script>

<style scoped>
@keyframes marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-100%); }
}

.animate-marquee {
  animation: marquee 35s linear infinite;
}

/* Optional: Pause on hover */
.group:hover .animate-marquee {
  animation-play-state: paused;
}
</style>
